import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ReactComponent as HelpCenterIcon } from 'assets/icons/circle-question-solid.svg';
import './PerformHelpcenterAction.scss';
import ReactTooltip from 'react-tooltip';
import { useClickedOutside } from 'services/Helper';
import { searchForHelpcenterArticles } from 'services/ProjectHttpService';
import { ProjectStore } from 'stores/private/ProjectStore';
import { inject, observer } from 'mobx-react';
import makeUrlFriendly from 'services/UrlHelper';
import { languages } from 'constants/Languages';
import { debounce } from 'lodash';

interface PerformHelpcenterActionProps {
  onArticleSelected: (article: any) => void;
  projectStore?: ProjectStore;
}

const PerformHelpcenterAction = ({
  onArticleSelected,
  projectStore,
}: PerformHelpcenterActionProps) => {
  const currentProject = projectStore?.currentProject;

  const helpcenterConfig = projectStore?.flowConfig.helpcenterConfig;
  const availableLanguages = languages.filter((language) =>
    helpcenterConfig?.localization.find(
      (item) => item.language === language.code,
    ),
  );

  const [selectedLanguage, setSelectedLanguage] = useState(
    availableLanguages[0],
  );

  const debounceSearch = useCallback(
    debounce((nextValue) => searchForArticles(nextValue), 750),
    [],
  );

  const [showArticleList, setShowArticleList] = useState(false);
  const [articles, setArticles] = useState([]);
  const [currentlyFocused, setCurrentlyFocused] = useState(0);

  const showArticleListRef = useRef(showArticleList);
  showArticleListRef.current = showArticleList;

  const articlesRef = useRef(articles);
  articlesRef.current = articles;

  const currentlyFocusedRef = useRef(0);
  currentlyFocusedRef.current = currentlyFocused;

  const wrapperRef = useRef(null);

  useClickedOutside(wrapperRef, () => {
    setShowArticleList(false);
  });

  const searchForArticles = async (searchTerm: string) => {
    const response = await searchForHelpcenterArticles({
      projectId: currentProject!.id,
      query: {
        searchTerm,
        lang: selectedLanguage.code,
      },
    });

    const preparedArticles = response.data.hits.map((article) => {
      return article.document;
    });

    setArticles(preparedArticles);
  };

  const handleArticleSelect = (article: any) => {
    const shareBaseURL = projectStore?.currentProject?.customDomainHelpCenter
      ? `https://${projectStore?.currentProject?.customDomainHelpCenter}`
      : `https://${projectStore?.currentProject?.defaultDomainHelpCenter}.gleap.help`;
    const articleURL = `${shareBaseURL}/${selectedLanguage.code}/articles/${
      article.docId
    }-${makeUrlFriendly(article.title)}`;

    onArticleSelected({ ...article, url: articleURL });
    setShowArticleList(false);
  };

  const handleUserKeyPress = useCallback((event: any) => {
    // Don't perform other key bindings if it's not open.
    if (!showArticleListRef.current) {
      return;
    }

    if (event.key === 'Escape') {
      setShowArticleList(false);
      event.preventDefault();
      return;
    }

    if (event.keyCode === 13) {
      if (showArticleListRef.current) {
        handleArticleSelect(articlesRef.current[currentlyFocusedRef.current]);
        event.preventDefault();
        return;
      }
    }

    if (event.keyCode === 40) {
      var newIndex = currentlyFocusedRef.current + 1;
      if (newIndex > articlesRef.current.length - 1) {
        newIndex = 0;
      }

      setCurrentlyFocused(newIndex);
      event.preventDefault();
      return;
    }

    if (event.keyCode === 38) {
      var newIndex = currentlyFocusedRef.current - 1;
      if (newIndex < 0) {
        newIndex = articlesRef.current.length - 1;
      }

      setCurrentlyFocused(newIndex);
      event.preventDefault();
      return;
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleUserKeyPress);
    return () => {
      window.removeEventListener('keydown', handleUserKeyPress);
    };
  }, [handleUserKeyPress]);

  const renderList = () => {
    return (
      <div className="helpcenter-list-wrapper">
        {articles.map((article: any, index: number) => {
          return (
            <div
              className={`helpcenter-list-item ${
                index === currentlyFocused && 'focused'
              }`}
              onMouseEnter={() => {
                setCurrentlyFocused(index);
              }}
              onClick={() => {
                handleArticleSelect(article);
              }}
            >
              <div className="helpcenter-list-item-title">{article.title}</div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="helpcenter-action-container" ref={wrapperRef}>
      {showArticleList && (
        <div className="article-list">
          <ReactTooltip
            id="mauLimitTooltip"
            className="infoTooltip"
            delayHide={300}
            type="light"
            effect="solid"
            getContent={(content) => {
              try {
                const data = JSON.parse(content);
                return (
                  <div className="ai-info-tooltip">
                    <b>{data.title}</b>
                    <br />
                    {data.description}
                  </div>
                );
              } catch (exp) {}
              return null;
            }}
          />
          <div className="article-list-title">
            <input
              className="article-list-title-input"
              type="text"
              autoFocus
              placeholder="Search"
              onChange={(text) => {
                debounceSearch(text.target.value);
              }}
            />
            {availableLanguages?.length > 1 && (
              <div className="language-select">
                <select
                  value={selectedLanguage.code}
                  onChange={(event) => {
                    const selectedValue = event.target.value;
                    const newlySelectedLanguage = availableLanguages.find(
                      (language) => language.code === selectedValue,
                    );

                    setSelectedLanguage(
                      newlySelectedLanguage ?? selectedLanguage,
                    );
                  }}
                >
                  {availableLanguages.map((language) => {
                    return (
                      <option value={language.code}>{language.name}</option>
                    );
                  })}
                </select>
              </div>
            )}
            <div className="article-list-title-tag">Helpcenter</div>
          </div>
          {renderList()}
        </div>
      )}
      {!showArticleList && (
        <ReactTooltip
          id="helpcenterButtonTooltip"
          className="infoTooltip infoTooltipButton"
          delayHide={0}
          type="light"
          effect="solid"
          getContent={(content) => {
            return (
              <div className="send-key-tooltip">
                <span>{content}</span>
              </div>
            );
          }}
        />
      )}
      <div
        data-for="helpcenterButtonTooltip"
        data-tip="Help center"
        className="helpcenter-container-item"
        onClick={() => {
          setShowArticleList(!showArticleList);
        }}
      >
        <HelpCenterIcon />
      </div>
    </div>
  );
};

export default inject('projectStore')(observer(PerformHelpcenterAction));
